/**
 * Download an HTML fragment as a Word-openable document (.doc).
 *
 * Used by the Behavior Incident Toolkit export surfaces (ISS reflection tasks,
 * student reflection forms, conversation guides, teacher summaries) so users
 * who are NOT on Google Docs can create an editable Word file directly, instead
 * of copy-pasting into Google Docs.
 *
 * Implementation note: we wrap the existing print/rich HTML in a Word-flavored
 * HTML document and serve it with the `application/msword` MIME type. Microsoft
 * Word, LibreOffice, and Pages all open this natively and preserve headings,
 * bold/italics, and lists — no extra dependency or server round-trip required.
 */

/** Ensure a filename ends in `.doc` (replacing a `.pdf`/other extension if present). */
export function toDocFilename(filename: string): string {
  const base = filename.replace(/\.[a-z0-9]+$/i, "");
  return `${base || "document"}.doc`;
}

export function downloadHtmlAsWord(bodyHtml: string, filename: string, title = "HeyBodhi document"): void {
  const safeTitle = title.replace(/[<>&]/g, "");
  const doc = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="utf-8" />
  <title>${safeTitle}</title>
  <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
  <style>
    body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #1f2937; }
    h1, h2, h3 { font-family: Calibri, Arial, sans-serif; }
  </style>
</head>
<body>${bodyHtml}</body>
</html>`;

  // Leading BOM keeps Word from mis-detecting the encoding.
  const blob = new Blob(["\ufeff", doc], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = toDocFilename(filename);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Defer revoke so the download has time to start.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
